/**
 * ViewContext — current top-level pane (chat / itinerary / booking / checkout).
 * v1 has no router; views are siblings under a single shell, with the bottom
 * nav switching state. Detail pages live inside each view (e.g. tap a booking
 * row → BookingDetail inside BookingView).
 *
 * Instances can turn the chat pane off entirely (feature_flags.chat === false,
 * e.g. a trip-planner-only embed). The guard lives HERE so every setView
 * caller (nav, host actions, empty-state CTAs, checkout back-links) redirects
 * to the Trip view instead of stranding the visitor on a hidden pane.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useInstance } from './InstanceContext';
import { emit as emitTelemetry } from '../utils/telemetry';

export type ViewId = 'chat' | 'itinerary' | 'booking' | 'checkout' | 'auth';

interface ViewContextValue {
  view: ViewId;
  setView: (v: ViewId) => void;
  /**
   * Return to the pane the visitor came from. Used by panes that are pushed
   * ON TOP of a flow rather than chosen from the nav — auth is opened by a
   * guest action (agent add, host add), so it needs a way out that does not
   * assume where the visitor started.
   *
   * Falls back to the nav's own default when there is no history (e.g. auth
   * was the first pane rendered), and never returns to 'auth' itself.
   */
  goBack: () => void;
  /** false when the instance disables the chat pane (feature_flags.chat === false) */
  chatEnabled: boolean;
}

const ViewContext = createContext<ViewContextValue | undefined>(undefined);

export const ViewProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { config } = useInstance();
  const chatEnabled =
    ((config?.feature_flags as Record<string, unknown> | undefined) || {})
      .chat !== false;
  const [view, setViewState] = useState<ViewId>('chat');
  // One step of history, in a ref rather than state: it never renders on its
  // own, and keeping it out of state avoids an extra commit on every nav.
  const prevViewRef = useRef<ViewId | null>(null);
  const setView = useCallback(
    (v: ViewId) => {
      const target: ViewId = v === 'chat' && !chatEnabled ? 'itinerary' : v;
      setViewState(prev => {
        if (prev !== target) {
          // Never remember 'auth' as a back target — going "back" into the
          // sign-in wall the visitor just escaped is a trap, not history.
          if (prev !== 'auth') prevViewRef.current = prev;
          emitTelemetry('view_changed', { props: { from: prev, to: target } });
        }
        return target;
      });
    },
    [chatEnabled],
  );
  const goBack = useCallback(() => {
    const fallback: ViewId = chatEnabled ? 'chat' : 'itinerary';
    const prev = prevViewRef.current;
    setView(prev && prev !== 'auth' ? prev : fallback);
  }, [chatEnabled, setView]);
  // Config resolves async — if chat lands disabled while we're sitting on the
  // default chat pane, hop to Trip.
  useEffect(() => {
    if (!chatEnabled) {
      setViewState(prev => (prev === 'chat' ? 'itinerary' : prev));
    }
  }, [chatEnabled]);
  const value = useMemo(
    () => ({ view, setView, goBack, chatEnabled }),
    [view, setView, goBack, chatEnabled],
  );
  return <ViewContext.Provider value={value}>{children}</ViewContext.Provider>;
};

export function useView(): ViewContextValue {
  const ctx = useContext(ViewContext);
  if (!ctx) throw new Error('useView must be used within ViewProvider');
  return ctx;
}
